import React from 'react';
import { Share2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useShareOrCopy } from '../../lib/useShareOrCopy';

interface ShareButtonProps {
    title: string;
    url: string;
    /** Layout classes merged onto the Button itself. Defaults to a top margin for a
        standalone placement; pass a layout class (e.g. "shrink-0") when rendering
        inline in the meta row. */
    className?: string;
}

/**
 * ShareButton — inline social-share affordance for blog posts.
 *
 * Visual: a primary (brand-primary filled) Button (Share2 icon + "Share" / "Copied!"
 * label) — identical styling to the gym-profile Share button. Placement is
 * caller-controlled via `className` (e.g. right-aligned in the article meta row).
 *
 * Behaviour comes from the shared useShareOrCopy hook (src/lib/useShareOrCopy.ts)
 * — Web Share API → clipboard fallback, central toast announce. The same hook
 * backs the gym-profile Share button; both consumers now render the same primary
 * visual over identical share behaviour.
 *
 * The button label flips to "Copied!" for 2s on the clipboard-fallback path
 * (local point-of-click feedback). The toast.success fires concurrently and
 * provides the aria-live announce — no local sr-only span needed here.
 */
const ShareButton: React.FC<ShareButtonProps> = ({ title, url, className = 'mt-8' }) => {
    const { status, share } = useShareOrCopy();

    return (
        <Button
            variant="primary"
            className={className}
            onClick={() => share({ title, url })}
        >
            <Share2 size={16} aria-hidden="true" className="mr-0.5 flex-shrink-0" />
            {status === 'copied' ? 'Copied!' : 'Share'}
        </Button>
    );
};

export default ShareButton;
